import React from 'react';
import { MilestoneManager } from './MilestoneManager';
import { SalesforceAccountSearch } from './SalesforceAccountSearch';
import { getSalesforceAccount } from '../../../API';



export const PracticeInfoForm = ({
  practiceName,
  setPracticeName,
  practiceUrl,
  setPracticeUrl,
  practicePhone,
  setPracticePhone,
  practiceLocation,
  setPracticeLocation,
  startDate,
  setStartDate,
  postSchedule,
  setPostSchedule,
  postsPerWeek,
  setPostsPerWeek,
  specialInstructions,
  setSpecialInstructions,
  milestones,
  setMilestones,
  onSubmit,
  isLoading,
  selectedSalesforceAccount,
  setSelectedSalesforceAccount,
}) => {
  const [hasUnsavedMilestone, setHasUnsavedMilestone] = React.useState(false);
  const [selectedAccount, setSelectedAccount] = React.useState(null);

  // Handle Salesforce account selection
  const handleAccountSelect = async (account) => {
    setSelectedAccount(account);
    // Store the full account object for upload functionality
    setSelectedSalesforceAccount(account);

    // Auto-populate fields from the search result
    if (account.Name) setPracticeName(account.Name);

    // Fetch full account details for additional fields
    try {
      const fullAccount = await getSalesforceAccount(account.Id);
      if (fullAccount) {
        if (fullAccount.Website) setPracticeUrl(fullAccount.Website);
        if (fullAccount.Phone) setPracticePhone(fullAccount.Phone);

        // Build location string from billing address
        const locationParts = [];
        if (fullAccount.BillingCity) locationParts.push(fullAccount.BillingCity);
        if (fullAccount.BillingState) locationParts.push(fullAccount.BillingState);
        if (locationParts.length > 0) {
          setPracticeLocation(locationParts.join(', '));
        }
      }
    } catch (error) {
      console.error('Error fetching account details:', error);
      // Continue with basic info from search result
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (hasUnsavedMilestone) {
      const confirmed = window.confirm(
        "You have unsaved milestone data. If you continue, it will be lost. Do you want to continue without adding this milestone?"
      );
      if (!confirmed) {
        return;
      }
    }

    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div className="post-planner-row-grid">
        <div className="post-planner-field">
          <label htmlFor="practiceName">Practice Name</label>
          <p>Search for a Salesforce account or type manually.</p>
          <SalesforceAccountSearch
            value={practiceName}
            onChange={setPracticeName}
            onAccountSelect={handleAccountSelect}
            placeholder="Search Salesforce or type practice name..."
            required
          />
        </div>
        <div className="post-planner-field">
          <label htmlFor="practiceUrl">Practice Website URL</label>
          <p>The full website address for the practice.</p>
          <input
            type="url"
            id="practiceUrl"
            value={practiceUrl}
            onChange={(e) => setPracticeUrl(e.target.value)}
            placeholder="e.g., https://www.gargle.com"
            className="post-planner-input"
            required
          />
        </div>
        <div className="post-planner-field">
          <label htmlFor="practicePhone">Practice Phone Number (Optional)</label>
          <p>Include the main phone number patients should call (local format is fine).</p>
          <input
            type="tel"
            id="practicePhone"
            value={practicePhone}
            onChange={(e) => setPracticePhone(e.target.value)}
            placeholder="e.g., (555) 123-4567"
            className="post-planner-input"
          />
        </div>
        <div className="post-planner-field">
          <label htmlFor="practiceLocation">Practice Location (Optional)</label>
          <p>If the practice has multiple locations, specify which location these posts are for.</p>
          <input
            type="text"
            id="practiceLocation"
            value={practiceLocation}
            onChange={(e) => setPracticeLocation(e.target.value)}
            placeholder="e.g., Lehi, UT or Lehi Office"
            className="post-planner-input"
          />
        </div>
        <div className="post-planner-field">
          <label htmlFor="startDate">Content Plan Start Date</label>
          <p>Select the date when posts should begin.</p>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="post-planner-input"
            required
            style={{ colorScheme: 'light' }}
          />
        </div>
        <div className="post-planner-field">
          <label>Posts Per Week</label>
          <p>Choose how many posts to generate per week.</p>
          <div className="post-planner-day-selector" style={{ display: 'flex', gap: '0.5rem', gridTemplateColumns: 'unset' }}>
            {[2, 3].map(num => (
              <button
                key={num}
                type="button"
                className={postsPerWeek === num ? 'is-selected' : ''}
                onClick={() => {
                  setPostsPerWeek(num);
                  // Reset post schedule when changing posts per week
                  setPostSchedule('');
                }}
                aria-pressed={postsPerWeek === num}
              >
                {num} Posts
              </button>
            ))}
          </div>
        </div>
        <div className="post-planner-field" style={{ gridColumn: '1 / -1' }}>
          <label>Post Schedule</label>
          <p>Choose which {postsPerWeek} days of the week posts will be published.</p>
          <div
            className="post-planner-day-selector"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
              gap: '0.5rem',
              width: '100%'
            }}
          >
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => {
              const dayCode = day.substring(0, 2).toUpperCase();
              const selectedDays = postSchedule ? postSchedule.split('-') : [];
              const isSelected = selectedDays.includes(dayCode);

              return (
                <button
                  key={day}
                  type="button"
                  className={isSelected ? 'is-selected' : ''}
                  onClick={() => {
                    const currentDays = postSchedule ? postSchedule.split('-') : [];
                    if (isSelected) {
                      // Remove this day
                      const newDays = currentDays.filter(d => d !== dayCode);
                      setPostSchedule(newDays.join('-'));
                    } else {
                      // Add this day - queue-based: if already at max, remove oldest (first), add new at end
                      let newDays;
                      if (currentDays.length >= postsPerWeek) {
                        // Remove oldest (first in array), add new one at end
                        newDays = [...currentDays.slice(1), dayCode];
                      } else {
                        // Just add to end
                        newDays = [...currentDays, dayCode];
                      }
                      // Don't sort - preserve click order for queue behavior
                      setPostSchedule(newDays.join('-'));
                    }
                  }}
                >
                  {day.substring(0, 3)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
        <div>
          <MilestoneManager
            milestones={milestones}
            setMilestones={setMilestones}
            onUnsavedDataChange={setHasUnsavedMilestone}
          />
        </div>

        <div className="post-planner-field">
          <label htmlFor="specialInstructions">Special Instructions for Post Generation (Optional)</label>
          <p>Provide specific instructions to guide the AI in creating your posts (e.g., "Use a friendly, conversational tone", "Include more patient testimonials", "Focus on family-friendly content").</p>
          <textarea
            id="specialInstructions"
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            rows={3}
            placeholder="e.g., Use a friendly tone, include more educational content about preventive care..."
            className="post-planner-textarea"
          />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-start', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
        <button
          type="submit"
          disabled={isLoading}
          className="post-planner-submit"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0.5rem 1.25rem',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: '600',
            borderRadius: '6px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            color: 'white',
            backgroundColor: isLoading ? '#d1d5db' : '#156fb9',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#125e9f')}
          onMouseOut={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#156fb9')}
        >
          {isLoading ? (
            <>
              <svg style={{ animation: 'spin 1s linear infinite', marginLeft: '-4px', marginRight: '10px', height: '14px', width: '14px', color: 'white' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generating...
            </>
          ) : (
            'Generate Content Plan'
          )}
        </button>
      </div>
    </form>
  );
};
